import { Divider, IconSend, Text } from 'braid-design-system';
import * as GraphQL from 'graphql';
import { InlineCode } from 'scoobie';

import { SchemaSection } from '#src/components/SchemaSection/SchemaSection.tsx';
import { definitionNameToEventType } from '#src/utils/definitionNameToEventType.ts';
import type { ObjectType } from '#src/utils/jsonSchema.ts';
import { getSortedJsonSchemaProperties } from '#src/utils/sort.ts';

import { PropertyDetails } from '../components/PropertyDetails.tsx';
import { SchemaMarkdown } from '../components/SchemaMarkdown.tsx';
import { TypeSnippet } from '../components/TypeSnippet.tsx';
import { schema } from '../schema.ts';
import { BasePage } from '#src/components/BasePage/BasePage.tsx';
import { useLocation } from 'react-router';

export const WebhookObjectSchemaPage = ({
  heading,
  objectName,
  objectType,
  propertyName,
}: {
  heading: string;
  objectName?: string;
  objectType: ObjectType;
  propertyName?: string;
}) => {
  const eventType = objectName && definitionNameToEventType(objectName);

  // Try to find a matching GraphQL event instance
  const graphqlEventObject =
    eventType && objectName && schema.getType(objectName);

  const path = objectName ? `webhook.${objectName}` : 'webhook';

  return (
    <BasePage
      title={{
        children: <InlineCode weight="weak">{heading}</InlineCode>,
        htmlTitle: heading,
        stringTitle: heading,
        subHeading: (
          <Text
            icon={<IconSend />}
            size="small"
            weight="medium"
            tone="secondary"
          >
            {eventType ? 'Webhook event payload' : 'Webhook JSON schema object'}
          </Text>
        ),
      }}
      breadcrumbs={[
        { href: '/schema', title: 'Schema' },
        { href: useLocation().pathname, title: heading },
      ]}
    >
      <SchemaMarkdown type={path} />

      <Divider />

      <SchemaSection heading="Properties">
        {getSortedJsonSchemaProperties(objectType).map(([name, type]) => (
          <PropertyDetails
            objectName={objectName}
            key={name}
            name={name}
            type={type}
            highlight={propertyName === name}
            required={objectType.required?.includes(name)}
            markdownPath={`${path}.${name}`}
          />
        ))}
      </SchemaSection>

      {GraphQL.isObjectType(graphqlEventObject) && (
        <SchemaSection heading="GraphQL event object">
          <TypeSnippet type={graphqlEventObject} />
        </SchemaSection>
      )}
    </BasePage>
  );
};
